import React, { useEffect, useRef } from "react"

// Wraps one toast card so it can be thrown off the screen with a finger.
// Mobile has no room for a dismiss button that isn't sitting on top of the
// game's HUD, so on touch the card itself is the control: drag it sideways and
// let go. See ToastContainer for where it is applied.

// How far a card has to travel before letting go dismisses it rather than
// springing it back. Proportional to the card, with a floor so a narrow toast
// isn't harder to throw away than a wide one.
const DISMISS_RATIO = 0.3
const DISMISS_MIN_DISTANCE = 70
// A flick counts even if it never got far — px per ms.
const DISMISS_VELOCITY = 0.45
// Travel before a touch stops being a tap and becomes a drag. Under this the
// gesture is still a click, so the buttons inside a toast keep working.
const DRAG_SLOP = 8
const FLING_MS = 190
const COLLAPSE_MS = 160

// The gap the toast stack lays its cards out with, cancelled while a card
// collapses so the ones below slide up instead of stepping up. Measured off
// the stack rather than hardcoded: the gap is a rem utility and index.css
// halves the root font-size under 1000px, which is the only width this gesture
// runs at — a fixed 16px cancelled twice the gap that was actually there and
// jerked the remaining cards up by the difference.
const stackGap = (el: HTMLElement) => {
  const parent = el.parentElement
  if (!parent) return 0
  const gap = parseFloat(window.getComputedStyle(parent).rowGap)
  return Number.isFinite(gap) ? gap : 0
}

interface SwipeableToastProps {
  /** Off on desktop, where the card keeps its dismiss button instead. */
  enabled: boolean
  onDismiss: () => void
  /**
   * The card runs its own exit (the completed-trade toast flies into the
   * trades panel). A swipe then hands over at the position the finger left it
   * rather than throwing the card off screen first.
   */
  customExit?: boolean
  children: React.ReactNode
}

export const SwipeableToast: React.FC<SwipeableToastProps> = ({
  enabled,
  onDismiss,
  customExit,
  children,
}) => {
  const elRef = useRef<HTMLDivElement>(null)
  // The press that is currently down: where it started, and a recent sample
  // (~30ms old) that the release velocity is measured against, so a slow drag
  // ending in a flick still reads as a flick.
  const pressRef = useRef<{
    id: number
    x: number
    y: number
    sampleX: number
    sampleT: number
  } | null>(null)
  const draggingRef = useRef(false)
  const dismissedRef = useRef(false)
  // Swallows the click that a browser fires after a drag, so ending a swipe
  // over "Accept invite" doesn't join the game.
  const swallowClickRef = useRef(false)
  const timersRef = useRef<number[]>([])

  useEffect(
    () => () => {
      timersRef.current.forEach((id) => window.clearTimeout(id))
    },
    []
  )

  const later = (fn: () => void, ms: number) => {
    timersRef.current.push(window.setTimeout(fn, ms))
  }

  const paint = (offset: number, animate: boolean) => {
    const el = elRef.current
    if (!el) return
    const width = el.offsetWidth || 320
    el.style.transition = animate
      ? `transform ${FLING_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${FLING_MS}ms ease-out`
      : "none"
    el.style.transform = offset ? `translateX(${offset}px)` : ""
    // Fades as it goes, so a half-hearted drag reads as "not gone yet".
    el.style.opacity = `${Math.max(0, 1 - Math.abs(offset) / (width * 1.1))}`
  }

  // Takes the card's height out of the stack after it has flown off, so the
  // toasts below move up smoothly instead of jumping into the empty slot.
  const collapse = () => {
    const el = elRef.current
    if (!el) {
      onDismiss()
      return
    }
    const gap = stackGap(el)
    el.style.transition = "none"
    el.style.height = `${el.offsetHeight}px`
    el.style.overflow = "hidden"
    el.style.pointerEvents = "none"
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const node = elRef.current
        if (!node) return
        node.style.transition = `height ${COLLAPSE_MS}ms ease, margin-bottom ${COLLAPSE_MS}ms ease`
        node.style.height = "0px"
        node.style.marginBottom = `-${gap}px`
      })
    })
    later(onDismiss, COLLAPSE_MS + 60)
  }

  const dismiss = (direction: 1 | -1) => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    const el = elRef.current
    if (!el || customExit) {
      onDismiss()
      return
    }
    paint(direction * (el.offsetWidth + 80), true)
    later(collapse, FLING_MS)
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled || dismissedRef.current) return
    if (e.pointerType === "mouse" && e.button !== 0) return
    pressRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      sampleX: e.clientX,
      sampleT: e.timeStamp,
    }
    draggingRef.current = false
    // A press that never became a drag must not eat its own click, and a drag
    // whose click never arrived must not eat the next tap's.
    swallowClickRef.current = false
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current
    if (!press || press.id !== e.pointerId || dismissedRef.current) return

    const dx = e.clientX - press.x
    const dy = e.clientY - press.y

    if (!draggingRef.current) {
      // Mostly vertical? Not our gesture — leave the press alone.
      if (Math.abs(dy) > DRAG_SLOP && Math.abs(dy) > Math.abs(dx)) {
        pressRef.current = null
        return
      }
      if (Math.abs(dx) < DRAG_SLOP) return
      draggingRef.current = true
      swallowClickRef.current = true
      // Re-base onto the finger so the card doesn't jump by the slop distance
      // the moment it starts following.
      press.x = e.clientX
      press.sampleX = e.clientX
      press.sampleT = e.timeStamp
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // Capture is a nicety; the gesture still works without it.
      }
      return
    }

    if (e.timeStamp - press.sampleT > 30) {
      press.sampleX = e.clientX
      press.sampleT = e.timeStamp
    }
    paint(e.clientX - press.x, false)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current
    pressRef.current = null
    if (!press || !draggingRef.current || dismissedRef.current) {
      draggingRef.current = false
      return
    }
    draggingRef.current = false

    const offset = e.clientX - press.x
    const velocity =
      (e.clientX - press.sampleX) / Math.max(1, e.timeStamp - press.sampleT)
    const width = elRef.current?.offsetWidth || 320
    const threshold = Math.max(DISMISS_MIN_DISTANCE, width * DISMISS_RATIO)
    const flicked =
      Math.abs(velocity) > DISMISS_VELOCITY && Math.abs(offset) > DRAG_SLOP * 2

    if (Math.abs(offset) > threshold || flicked) {
      dismiss((offset || velocity) > 0 ? 1 : -1)
    } else {
      paint(0, true)
    }
  }

  const handlePointerCancel = () => {
    pressRef.current = null
    if (!draggingRef.current || dismissedRef.current) return
    draggingRef.current = false
    paint(0, true)
  }

  if (!enabled) return <div>{children}</div>

  return (
    <div
      ref={elRef}
      // pan-y keeps the browser out of horizontal gestures on this card while
      // leaving anything vertical to the page.
      className="touch-pan-y select-none will-change-transform"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClickCapture={(e) => {
        if (!swallowClickRef.current) return
        swallowClickRef.current = false
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {children}
    </div>
  )
}
